// components/process-units/Sweetening.js
import React, { useState } from 'react';

const Sweetening = ({ config, updateConfig }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Handle input changes
  const handleInputChange = (field, value) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      updateConfig({
        [field]: numValue
      });
    }
  };

  return (
    <div className="process-unit-card">
      <div 
        className="unit-header" 
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h3>Sweetening (Acid Gas Removal)</h3>
        <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
          {isExpanded ? '▼' : '▶'}
        </span>
      </div>
      
      {isExpanded && (
        <div className="unit-content">
          <p className="unit-description">
            Sweetening removes acid gases (H₂S, CO₂) from natural gas using absorption and regeneration processes.
            Amine solutions like MDEA or physical solvents like Sulfinol are commonly used to meet pipeline 
            specifications and protect downstream equipment.
          </p>
          
          <div className="parameter-group">
            <div className="parameter-row">
              <label>Solvent Type</label>
              <select 
                value={config.solventType || 'mdea'}
                onChange={(e) => updateConfig({ solventType: e.target.value })}
              >
                <option value="mdea">MDEA (Methyldiethanolamine)</option>
                <option value="dea">DEA (Diethanolamine)</option>
                <option value="mea">MEA (Monoethanolamine)</option>
                <option value="sulfinol">Sulfinol</option>
                <option value="selexol">Selexol</option>
              </select>
            </div>
            
            <div className="parameter-row">
              <label>H₂S Inlet Content (ppm)</label>
              <input
                type="number"
                min="0"
                max="100000"
                step="100"
                value={config.h2sInlet || 1000}
                onChange={(e) => handleInputChange('h2sInlet', e.target.value)}
              />
            </div>
            
            <div className="parameter-row">
              <label>CO₂ Inlet Content (mol%)</label>
              <input
                type="number"
                min="0"
                max="50"
                step="0.1"
                value={config.co2Inlet || 3}
                onChange={(e) => handleInputChange('co2Inlet', e.target.value)}
              />
            </div>
            
            <div className="parameter-row">
              <label>H₂S Outlet Spec (ppm)</label>
              <input
                type="number"
                min="0"
                max="1000"
                step="1"
                value={config.h2sOutlet || 4}
                onChange={(e) => handleInputChange('h2sOutlet', e.target.value)}
              />
            </div>
            
            <div className="parameter-row">
              <label>CO₂ Outlet Spec (mol%)</label>
              <input
                type="number"
                min="0"
                max="5"
                step="0.1"
                value={config.co2Outlet || 2}
                onChange={(e) => handleInputChange('co2Outlet', e.target.value)}
              />
            </div>
            
            <div className="parameter-row">
              <label>Solvent Circulation Rate (m³/h)</label>
              <input
                type="number"
                min="1"
                max="1000"
                step="1"
                value={config.solventRate || 100}
                onChange={(e) => handleInputChange('solventRate', e.target.value)}
              />
            </div>
            
            <div className="parameter-row">
              <label>Absorber Pressure (bar)</label>
              <input
                type="number"
                min="10"
                max="120"
                step="1"
                value={config.absorberPressure || 70}
                onChange={(e) => handleInputChange('absorberPressure', e.target.value)}
              />
            </div>
            
            <div className="parameter-row">
              <label>Regenerator Pressure (bar)</label>
              <input
                type="number"
                min="1"
                max="5"
                step="0.1"
                value={config.regeneratorPressure || 1.5}
                onChange={(e) => handleInputChange('regeneratorPressure', e.target.value)}
              />
            </div>
            
            <div className="parameter-row">
              <label>Reboiler Temperature (°C)</label>
              <input
                type="number"
                min="100"
                max="150"
                step="1"
                value={config.reboilerTemp || 120}
                onChange={(e) => handleInputChange('reboilerTemp', e.target.value)}
              />
            </div>
            
            <div className="parameter-row">
              <label>Acid Gas Disposal</label>
              <select 
                value={config.acidGasDisposal || 'sru'}
                onChange={(e) => updateConfig({ acidGasDisposal: e.target.value })}
              >
                <option value="sru">Sulfur Recovery Unit</option>
                <option value="incineration">Incineration</option>
                <option value="injection">Acid Gas Injection</option>
                <option value="vent">Vent (CO₂ only streams)</option>
              </select>
            </div>
          </div>
          
          <div className="unit-info-box">
            <h4>Engineering Notes</h4>
            <ul>
              <li>MDEA is selective for H₂S over CO₂ and has lower regeneration energy.</li>
              <li>MEA has higher acid gas loading capacity but requires more regeneration energy.</li>
              <li>Sulfinol combines chemical and physical absorption for high-pressure applications.</li>
              <li>Major energy consumers: reboiler (steam), rich amine pumps, and lean/rich exchangers.</li>
              <li>Reboiler duty typically accounts for 70-80% of the total energy consumption in amine units.</li>
              <li>The regeneration energy is roughly proportional to the amount of acid gas removed.</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sweetening;